import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Car, Download } from "lucide-react";
import { toast } from "sonner";
import { CustomerCombobox } from "@/components/customers/CustomerCombobox";
import { PRESETS } from "@/components/expenses/ExpenseTable";
import { TripFormDialog } from "@/components/mileage/TripFormDialog";
import { TripTable } from "@/components/mileage/TripTable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { period: "this_year", start_date: PRESETS.this_year()[0], end_date: PRESETS.this_year()[1], customer_id: "", q: "" };

export default function Mileage() {
  const [filters, setFilters] = useState(EMPTY);
  const [data, setData] = useState({ trips: [], count: 0, total_miles: 0, total_deduction: 0, by_customer: [] });
  const [customers, setCustomers] = useState([]);
  const [rates, setRates] = useState({ rates: [], current_rate: 0 });
  const [form, setForm] = useState({ open: false, trip: null });

  const load = useCallback(async () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([k, v]) => v && k !== "period"));
    try { const { data } = await axios.get(`${API}/mileage`, { params }); setData(data); } catch (e) { toast.error(e.response?.data?.detail || "Could not load trips"); }
  }, [filters]);
  useEffect(() => {
    axios.get(`${API}/customers`).then((r) => setCustomers(r.data)).catch(() => {});
    axios.get(`${API}/mileage/rates`).then((r) => setRates(r.data)).catch(() => {});
  }, []);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const set = (patch) => setFilters({ ...filters, ...patch });
  const setPeriod = (p) => { const f = PRESETS[p]; set(f ? { period: p, start_date: f()[0], end_date: f()[1] } : { period: p }); };
  const remove = async (t) => {
    if (!window.confirm(`Delete trip "${t.purpose}" (${t.miles} mi)?`)) return;
    try { await axios.delete(`${API}/mileage/${t.id}`); toast.success("Trip deleted"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const exportCsv = async () => {
    const params = { ...Object.fromEntries(Object.entries(filters).filter(([k, v]) => v && k !== "period")), format: "csv" };
    try {
      const res = await axios.get(`${API}/mileage`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = "mileage-log.csv"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  return (
    <div data-testid="mileage-page">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 mb-1 flex items-center gap-3"><Car className="w-8 h-8 text-blue-600" /> Mileage
            <Badge variant="outline" className="font-mono" data-testid="mileage-current-rate">IRS ${Number(rates.current_rate || 0).toFixed(3)}/mi</Badge></h1>
          <p className="text-slate-600">IRS-compliant trip log. Deductions flow to Schedule C line 9 and Profit &amp; Loss automatically.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCsv} data-testid="mileage-export-csv"><Download className="w-4 h-4 mr-2" /> CSV log</Button>
          <Button onClick={() => setForm({ open: true, trip: null })} className="bg-blue-600 hover:bg-blue-700" data-testid="add-trip-button"><Plus className="w-4 h-4 mr-2" /> Log trip</Button>
        </div>
      </div>
      <div className="space-y-4">
        <Card className="bg-white border-0 shadow-lg" data-testid="mileage-filters"><CardContent className="p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
          <div><Label>Period</Label><Select value={filters.period} onValueChange={setPeriod}><SelectTrigger data-testid="mileage-filter-period"><SelectValue /></SelectTrigger>
            <SelectContent>{[["all", "All time"], ["this_month", "This month"], ["last_month", "Last month"], ["this_quarter", "This quarter"], ["this_year", "This year"], ["custom", "Custom"]].map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>From</Label><Input type="date" value={filters.start_date} onChange={(e) => set({ start_date: e.target.value, period: "custom" })} data-testid="mileage-filter-start" /></div>
          <div><Label>To</Label><Input type="date" value={filters.end_date} onChange={(e) => set({ end_date: e.target.value, period: "custom" })} data-testid="mileage-filter-end" /></div>
          <div><Label>Customer</Label><CustomerCombobox customers={customers} value={filters.customer_id} onChange={(id) => set({ customer_id: id || "" })} allowAll placeholder="All customers" testId="mileage-filter-customer" /></div>
          <div><Label>Search</Label><Input value={filters.q} onChange={(e) => set({ q: e.target.value })} placeholder="Purpose, place, vehicle…" data-testid="mileage-filter-search" /></div>
        </CardContent></Card>
        <TripTable data={data} onEdit={(t) => setForm({ open: true, trip: t })} onDelete={remove} />
        {data.by_customer?.length > 0 && <div className="flex flex-wrap gap-2" data-testid="mileage-by-customer">{data.by_customer.slice(0, 8).map((g) => <Badge key={g.customer} variant="outline" className="bg-white">{g.customer}: {g.miles.toFixed(1)} mi</Badge>)}</div>}
      </div>
      <TripFormDialog open={form.open} onOpenChange={(o) => setForm({ ...form, open: o })} trip={form.trip} customers={customers} rates={rates.rates} onSaved={load} />
    </div>
  );
}
